import {
  getAddress,
  hexToBigInt,
  isAddress,
  keccak256,
  numberToHex,
  type Address,
} from "viem"
import { useContractRead } from "wagmi"

import { getLSTStakerConstants } from "@/governance/constants/lststaker"
import {
  useAccountsByAddressQuery,
  type AccountsByAddressQuery,
} from "@/query/graphql"
import { chainIdToChainReference } from "@/web3/helpers/transformers"
import { isSolanaAddress } from "@/web3/helpers/address"

type Args = {
  orgId?: string
  accountAddress?: Address
}
type Account = AccountsByAddressQuery["accounts"][number]

export type DelegateeData = {
  account?: Account
  isLoading: boolean
  update: () => void
}

const useDelegateeAddress = (orgId?: string, accountAddress?: Address) => {
  const {
    rebasingLSTToken,
    lstToken,
    chainId,
    useGetAliasedDelegateeFromRebasingContract,
  } = getLSTStakerConstants(orgId)
  const chainReference = chainIdToChainReference(chainId)

  // New test alias implementation
  // https://github.com/withtally/stGOV/blob/c9a6005ecd04a5442edbb5664588ae681e41335e/src/FixedLstAddressAlias.sol
  const aliasedAddress1 = fixedAlias(accountAddress as Address)

  const { data: defaultDelegatee, isLoading: defaultDelegateeIsLoading } =
    useContractRead({
      address: rebasingLSTToken.address as Address,
      abi: rebasingLSTToken.factory.abi,
      functionName: "defaultDelegatee",
      chainId: chainReference,
    })

  const {
    data: delegatee1,
    isLoading: isLoading1,
    refetch: refetch1,
  } = useContractRead({
    address: rebasingLSTToken.address as Address,
    abi: rebasingLSTToken.factory.abi,
    functionName: "delegateeForHolder",
    args: [aliasedAddress1],
    chainId: chainReference,
    enabled:
      Boolean(aliasedAddress1) && useGetAliasedDelegateeFromRebasingContract,
  })

  const {
    data: delegatee2,
    isLoading: isLoading2,
    refetch: refetch2,
  } = useContractRead({
    address: lstToken.address as Address,
    abi: lstToken.factory.abi,
    functionName: "delegateeForHolder",
    args: [accountAddress],
    chainId: chainReference,
    enabled:
      Boolean(accountAddress) && !useGetAliasedDelegateeFromRebasingContract,
  })

  const delegateeOptions = [delegatee1, delegatee2] as string[]

  // If the aliased address is invalid, the default delegate will be returned
  // So we return the first non-default delegate
  const delegateeAddress = delegateeOptions.filter((d) => isAddress(d))

  const nonDefaultDelegatee = delegateeAddress.find(
    (d) => d !== defaultDelegatee,
  )

  return {
    address: nonDefaultDelegatee ?? delegateeAddress[0],
    isLoading: defaultDelegateeIsLoading || isLoading1 || isLoading2,
    update: () => {
      refetch1()
      refetch2()
    },
  }
}

export function useLSTDelegatee({
  orgId,
  accountAddress,
}: Args): DelegateeData {
  const {
    address: delegateeAddress,
    isLoading,
    update,
  } = useDelegateeAddress(orgId, accountAddress)

  const {
    data: _delegatee,
    refetch: refetchDelegatee,
    isLoading: isLoadingDelegatee,
  } = useAccountsByAddressQuery(
    {
      addresses: [delegateeAddress as Address],
    },
    { enabled: Boolean(delegateeAddress) },
  )

  const account = _delegatee?.accounts[0]

  return {
    account,
    isLoading: isLoading || isLoadingDelegatee,
    update: () => {
      refetchDelegatee()
      update()
    },
  }
}

/**
  Weird workaround to get the delegatee for the address
  Scopelift bug was found. 
  https://tally-network.slack.com/archives/C075A7DTNL8/p1744148644187489
  https://github.com/withtally/stGOV/blob/main/src/FixedLstAddressAlias.sol
 */

function fixedAlias(account: Address) {
  const ALIAS_OFFSET_SALT = "0x1000010100000110011011010010"
  try {
    if (isSolanaAddress(account)) return null

    // 1. Convert address to uint160 (remove checksum and ensure it's a number)
    const addressAsNumber = hexToBigInt(account)

    // 2. Add the salt (this matches the Solidity uint160(_account) + ALIAS_OFFSET_SALT)
    const saltBigInt = hexToBigInt(ALIAS_OFFSET_SALT)
    const sum = addressAsNumber + saltBigInt

    // 3. Convert to hex and ensure proper padding (matches abi.encode(uint160))
    const paddedHex = numberToHex(sum, { size: 32 }) // Use 32 bytes for full uint256

    // 4. Hash it (matches keccak256)
    const hashed = keccak256(paddedHex)

    // 5. Take last 20 bytes and convert to address (matches address(uint160(uint256(...))))
    const alias = `0x${hashed.slice(-40)}`

    // 6. Convert to checksum address
    return getAddress(alias)
  } catch (error) {
    console.error("Error in fixedAlias2:", error)

    return null
  }
}
